import { useMemo } from 'react';
import { useAppStore } from '../stores/app.store';
import { useStagesStore } from '../stores/stages.store';
import type { Agent, Stage } from '../types';

// Sprint agent-stages — sections are now founder-defined workflow stages
// (spec D3), replacing the recency buckets (pinned/today/yesterday/older,
// Layer 5 D54 / Layer 6 D56). Fence: the recency design organized by
// "when did I touch this"; Santi's approved redesign (chat 2026-07-17)
// organizes by "what phase is this in". Behavior retired, data intact
// (`pinned` column + index survive).
//
// Section keys: each stage's id, plus the two fixed keys below.
export const NO_STAGE_KEY = 'noStage';
export const ARCHIVED_KEY = 'archived';

export interface StageSection {
  key: string; // stage.id | NO_STAGE_KEY
  stage: Stage | null; // null for the "Sin estado" inbox
  agents: Agent[];
}

export interface GroupedAgents {
  // Stage sections in sortOrder, then the "Sin estado" inbox. Stage
  // sections with zero agents are INCLUDED (they render as drop targets
  // and keep the founder's stage list visible); the Sidebar decides
  // whether to hide empty ones (e.g. while searching).
  sections: StageSection[];
  archived: Agent[];
}

function lastActivityMs(a: Agent): number {
  const t = Date.parse(a.lastActivityAt);
  return Number.isFinite(t) ? t : 0;
}

function sortByActivityDesc(a: Agent, b: Agent): number {
  return lastActivityMs(b) - lastActivityMs(a);
}

/**
 * Pure bucketing logic — exported for testing without a React tree.
 *
 * Bucketing rules (sprint agent-stages, spec D3/D8):
 *   stage section: archived=false AND stageId resolves to a loaded stage
 *   noStage:       archived=false AND (stageId null OR unresolvable)
 *   archived:      archived=true (regardless of stageId)
 *
 * Conservation invariant (spec D8 / AC6): every non-deleted agent that
 * passes the search + project filters lands in EXACTLY one bucket — an
 * unresolvable stageId (e.g. a stage deleted from another device before
 * this tab reloaded) falls back to noStage, never disappears.
 *
 * Sprint dispatch-lineage-ui (D2) — ONE amendment to that invariant: a
 * DISPATCHED session (`dispatchedBy` non-null — a worker another agent
 * started through `/dispatch`) lands in NO bucket while the search is
 * empty. Santi, 2026-09-09: "eso me genera demasiado ruido; me lleva
 * tiempo saber si cierro esas conversaciones o si han terminado; no
 * necesito esas decisiones". It is presented where it belongs — as a
 * card inside its caller's conversation (`DispatchWorkerCard`). An
 * explicit text search that matches its name is the only door back: it
 * then falls into its normal bucket, badged "Despachada por <caller>".
 *
 * All sections sorted by lastActivityAt DESC. When searchQuery is
 * non-empty, filters by case-insensitive substring match. Optional
 * projectId narrows to that workspace (Sprint 11b).
 */
export function groupAgents(
  agents: Record<string, Agent>,
  searchQuery: string,
  stages: Stage[],
  projectId: string | null = null,
): GroupedAgents {
  const q = searchQuery.trim().toLowerCase();

  const byStage = new Map<string, Agent[]>();
  for (const s of stages) byStage.set(s.id, []);
  const noStage: Agent[] = [];
  const archived: Agent[] = [];

  for (const a of Object.values(agents)) {
    if (a.deleted) continue;
    // D2 — a dispatched worker is invisible unless explicitly searched for.
    if (a.dispatchedBy && !q) continue;
    if (q && !a.name.toLowerCase().includes(q)) continue;
    if (projectId && a.projectId !== projectId) continue;

    if (a.archived) {
      archived.push(a);
      continue;
    }
    const bucket = a.stageId != null ? byStage.get(a.stageId) : undefined;
    if (bucket) {
      bucket.push(a);
    } else {
      noStage.push(a); // D8 fallback — includes dangling stageIds
    }
  }

  const sections: StageSection[] = [
    ...[...stages]
      .sort((a, b) => a.sortOrder - b.sortOrder || a.id.localeCompare(b.id))
      .map((stage) => ({
        key: stage.id,
        stage,
        agents: (byStage.get(stage.id) ?? []).sort(sortByActivityDesc),
      })),
    { key: NO_STAGE_KEY, stage: null, agents: noStage.sort(sortByActivityDesc) },
  ];
  archived.sort(sortByActivityDesc);

  return { sections, archived };
}

/**
 * Sidebar data source: agents bucketed into stage sections + "Sin estado"
 * + "Archivado". Memoized on the agents map identity, searchQuery, the
 * stages list, and the optional projectId filter.
 */
export function useGroupedAgents(opts?: { projectId?: string | null }): GroupedAgents {
  const agentsMap = useAppStore((s) => s.agents);
  const searchQuery = useAppStore((s) => s.searchQuery);
  const stages = useStagesStore((s) => s.stages);
  const projectId = opts?.projectId ?? null;

  return useMemo(
    () => groupAgents(agentsMap, searchQuery, stages, projectId),
    [agentsMap, searchQuery, stages, projectId],
  );
}
